import { Box } from "@mui/material";
import type { ReactNode } from "react";

// Asset metadata from pad_asset_metadata.json
const PAD_SIZE = { width: 956, height: 1324 };
const SCREEN_RECT = { left: 109, top: 54, width: 724, height: 1113 };
const SCREEN_CORNER_RADIUS = 30;

interface HoloPadProps {
  children?: ReactNode;
  onClick?: () => void;
  sx?: object;
}

/**
 * A sci-fi holo-pad component with layered display:
 * 1. Content layer (bottom) - children rendered in the screen area
 * 2. Glass overlay (middle) - semi-transparent glass effect
 * 3. Bezel + glitch animation (top) - animated WebP with transparent screen
 */
export function HoloPad({ children, onClick, sx }: HoloPadProps) {
  // Calculate percentages for responsive positioning
  const screenLeft = (SCREEN_RECT.left / PAD_SIZE.width) * 100;
  const screenTop = (SCREEN_RECT.top / PAD_SIZE.height) * 100;
  const screenWidth = (SCREEN_RECT.width / PAD_SIZE.width) * 100;
  const screenHeight = (SCREEN_RECT.height / PAD_SIZE.height) * 100;
  // Corner radius as percentage of screen width for scaling
  const radiusPercent = (SCREEN_CORNER_RADIUS / SCREEN_RECT.width) * 100;

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: `${PAD_SIZE.width} / ${PAD_SIZE.height}`,
        cursor: onClick ? "pointer" : "default",
        ...sx,
      }}
    >
      {/* Layer 1: Content area (bottom) */}
      <Box
        sx={{
          position: "absolute",
          left: `${screenLeft}%`,
          top: `${screenTop}%`,
          width: `${screenWidth}%`,
          height: `${screenHeight}%`,
          borderRadius: `${radiusPercent}%`,
          overflow: "hidden",
          backgroundColor: "rgba(0, 10, 20, 0.9)",
          color: "#00ffcc",
          fontFamily: "'Courier New', monospace",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "3%",
          boxSizing: "border-box",
          containerType: "size",
        }}
      >
        {children}
      </Box>

      {/* Layer 2: Scanline effect */}
      <Box
        sx={{
          position: "absolute",
          left: `${screenLeft}%`,
          top: `${screenTop}%`,
          width: `${screenWidth}%`,
          height: `${screenHeight}%`,
          borderRadius: `${radiusPercent}%`,
          background:
            "repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0px, rgba(0,0,0,0.15) 1px, transparent 1px, transparent 3px)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      {/* Layer 3: Glass overlay - covers full pad */}
      <Box
        component="img"
        src="/screen_glass_overlay.png"
        alt=""
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      />

      {/* Layer 3: Bezel with glitch animation (top) */}
      <Box
        component="img"
        src="/pad_glitch.webp"
        alt="Holo-pad device"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}
